import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';

const CodeGeneratorModal = ({ isOpen, onClose, onGenerate }) => {
    const [activeTab, setActiveTab] = useState('qr'); // 'qr' or 'barcode'
    const [qrType, setQrType] = useState('url'); // text, url, email, vcard, wifi
    const [barcodeFormat, setBarcodeFormat] = useState('CODE128');

    // QR Data States
    const [qrText, setQrText] = useState('');
    const [qrUrl, setQrUrl] = useState('https://');
    const [qrEmail, setQrEmail] = useState('');
    const [qrVCard, setQrVCard] = useState({
        firstName: '',
        lastName: '',
        organization: '',
        phone: '',
        email: '',
        website: ''
    });
    const [qrWifi, setQrWifi] = useState({
        ssid: '',
        password: '',
        encryption: 'WPA'
    });

    // Barcode Data State
    const [barcodeValue, setBarcodeValue] = useState('1234567890');

    // Preview State
    const [previewUrl, setPreviewUrl] = useState(null);

    useEffect(() => {
        if (isOpen) {
            generatePreview();
        }
    }, [isOpen, activeTab, qrType, barcodeFormat, qrText, qrUrl, qrEmail, qrVCard, qrWifi, barcodeValue]);

    const generatePreview = async () => {
        try {
            if (activeTab === 'qr') {
                let content = '';
                switch (qrType) {
                    case 'text':
                        content = qrText;
                        break;
                    case 'url':
                        content = qrUrl;
                        break;
                    case 'email':
                        content = `mailto:${qrEmail}`;
                        break;
                    case 'vcard':
                        content = `BEGIN:VCARD\nVERSION:3.0\nN:${qrVCard.lastName};${qrVCard.firstName}\nFN:${qrVCard.firstName} ${qrVCard.lastName}\nORG:${qrVCard.organization}\nTEL:${qrVCard.phone}\nEMAIL:${qrVCard.email}\nURL:${qrVCard.website}\nEND:VCARD`;
                        break;
                    case 'wifi':
                        content = `WIFI:T:${qrWifi.encryption};S:${qrWifi.ssid};P:${qrWifi.password};;`;
                        break;
                    default:
                        content = qrText;
                }

                if (!content) {
                    setPreviewUrl(null);
                    return;
                }

                const url = await QRCode.toDataURL(content, { margin: 1, width: 200 });
                setPreviewUrl(url);
            } else {
                if (!barcodeValue) {
                    setPreviewUrl(null);
                    return;
                }
                const canvas = document.createElement('canvas');
                JsBarcode(canvas, barcodeValue, {
                    format: barcodeFormat,
                    displayValue: true,
                    margin: 10
                });
                setPreviewUrl(canvas.toDataURL('image/png'));
            }
        } catch (error) {
            console.error("Error generating preview:", error);
            setPreviewUrl(null);
        }
    };

    const handleGenerate = () => {
        let finalData = '';
        let finalType = activeTab === 'qr' ? 'qrCode' : 'barcode1d';
        let finalCodeType = qrType;
        let finalFormat = barcodeFormat;

        if (activeTab === 'qr') {
            switch (qrType) {
                case 'text': finalData = qrText; break;
                case 'url': finalData = qrUrl; break;
                case 'email': finalData = qrEmail; break; // Raw email, logic handles mailto:
                case 'vcard':
                    finalData = `BEGIN:VCARD\nVERSION:3.0\nN:${qrVCard.lastName};${qrVCard.firstName}\nFN:${qrVCard.firstName} ${qrVCard.lastName}\nORG:${qrVCard.organization}\nTEL:${qrVCard.phone}\nEMAIL:${qrVCard.email}\nURL:${qrVCard.website}\nEND:VCARD`;
                    break;
                case 'wifi':
                    finalData = `WIFI:T:${qrWifi.encryption};S:${qrWifi.ssid};P:${qrWifi.password};;`;
                    break;
            }
        } else {
            finalData = barcodeValue;
        }

        onGenerate(finalType, finalData, finalCodeType, finalFormat);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                    <h3 className="font-bold text-lg text-gray-800">Generador de Códigos</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* Sidebar / Tabs */}
                    <div className="w-1/3 bg-gray-50 border-r border-gray-200 p-4 space-y-2 overflow-y-auto">
                        <div className="flex space-x-2 mb-6 p-1 bg-gray-200 rounded-lg">
                            <button
                                className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${activeTab === 'qr' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                                onClick={() => setActiveTab('qr')}
                            >
                                QR Code
                            </button>
                            <button
                                className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${activeTab === 'barcode' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                                onClick={() => setActiveTab('barcode')}
                            >
                                Código de Barras
                            </button>
                        </div>

                        {activeTab === 'qr' && (
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Tipo de Contenido</label>
                                {[
                                    { id: 'url', label: 'Sitio Web (URL)', icon: '🔗' },
                                    { id: 'text', label: 'Texto Simple', icon: '📝' },
                                    { id: 'email', label: 'Correo Electrónico', icon: '✉️' },
                                    { id: 'vcard', label: 'Tarjeta de Contacto', icon: '📇' },
                                    { id: 'wifi', label: 'Red Wi-Fi', icon: '📶' }
                                ].map(type => (
                                    <button
                                        key={type.id}
                                        onClick={() => setQrType(type.id)}
                                        className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center space-x-2 transition-colors ${qrType === type.id ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-700'}`}
                                    >
                                        <span>{type.icon}</span>
                                        <span>{type.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {activeTab === 'barcode' && (
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Formato</label>
                                    <select
                                        value={barcodeFormat}
                                        onChange={(e) => setBarcodeFormat(e.target.value)}
                                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm text-gray-900"
                                    >
                                        <option value="CODE128">CODE128 (Estándar)</option>
                                        <option value="CODE39">CODE39</option>
                                        <option value="EAN13">EAN13</option>
                                        <option value="UPC">UPC</option>
                                        <option value="ITF14">ITF14</option>
                                        <option value="MSI">MSI</option>
                                        <option value="pharmacode">Pharmacode</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 p-6 overflow-y-auto bg-white">
                        <div className="mb-6">
                            <h4 className="text-lg font-medium text-gray-900 mb-4">
                                {activeTab === 'qr' ? 'Configurar QR' : 'Configurar Código de Barras'}
                            </h4>

                            {/* QR Inputs */}
                            {activeTab === 'qr' && (
                                <div className="space-y-4">
                                    {qrType === 'url' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">URL del Sitio Web</label>
                                            <input
                                                type="url"
                                                value={qrUrl}
                                                onChange={(e) => setQrUrl(e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                                placeholder="https://www.ejemplo.com"
                                            />
                                        </div>
                                    )}

                                    {qrType === 'text' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Texto</label>
                                            <textarea
                                                value={qrText}
                                                onChange={(e) => setQrText(e.target.value)}
                                                rows={4}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                                placeholder="Escribe tu texto aquí..."
                                            />
                                        </div>
                                    )}

                                    {qrType === 'email' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Dirección de Correo</label>
                                            <input
                                                type="email"
                                                value={qrEmail}
                                                onChange={(e) => setQrEmail(e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                                placeholder="usuario@ejemplo.com"
                                            />
                                        </div>
                                    )}

                                    {qrType === 'vcard' && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="col-span-1">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                                                <input
                                                    type="text"
                                                    value={qrVCard.firstName}
                                                    onChange={(e) => setQrVCard({ ...qrVCard, firstName: e.target.value })}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                                />
                                            </div>
                                            <div className="col-span-1">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Apellidos</label>
                                                <input
                                                    type="text"
                                                    value={qrVCard.lastName}
                                                    onChange={(e) => setQrVCard({ ...qrVCard, lastName: e.target.value })}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Organización / Empresa</label>
                                                <input
                                                    type="text"
                                                    value={qrVCard.organization}
                                                    onChange={(e) => setQrVCard({ ...qrVCard, organization: e.target.value })}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                                />
                                            </div>
                                            <div className="col-span-1">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                                                <input
                                                    type="tel"
                                                    value={qrVCard.phone}
                                                    onChange={(e) => setQrVCard({ ...qrVCard, phone: e.target.value })}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                                />
                                            </div>
                                            <div className="col-span-1">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                                <input
                                                    type="email"
                                                    value={qrVCard.email}
                                                    onChange={(e) => setQrVCard({ ...qrVCard, email: e.target.value })}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Sitio Web</label>
                                                <input
                                                    type="url"
                                                    value={qrVCard.website}
                                                    onChange={(e) => setQrVCard({ ...qrVCard, website: e.target.value })}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {qrType === 'wifi' && (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de Red (SSID)</label>
                                                <input
                                                    type="text"
                                                    value={qrWifi.ssid}
                                                    onChange={(e) => setQrWifi({ ...qrWifi, ssid: e.target.value })}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                                                <input
                                                    type="text"
                                                    value={qrWifi.password}
                                                    onChange={(e) => setQrWifi({ ...qrWifi, password: e.target.value })}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Encriptación</label>
                                                <select
                                                    value={qrWifi.encryption}
                                                    onChange={(e) => setQrWifi({ ...qrWifi, encryption: e.target.value })}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                                >
                                                    <option value="WPA">WPA/WPA2</option>
                                                    <option value="WEP">WEP</option>
                                                    <option value="nopass">Ninguna</option>
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Barcode Inputs */}
                            {activeTab === 'barcode' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor del Código</label>
                                    <input
                                        type="text"
                                        value={barcodeValue}
                                        onChange={(e) => setBarcodeValue(e.target.value)}
                                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono text-lg text-gray-900"
                                        placeholder="1234567890"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Preview Area */}
                        <div className="border-t border-gray-200 pt-6">
                            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">Vista Previa</h4>
                            <div className="flex justify-center bg-gray-100 p-6 rounded-lg border border-gray-200">
                                {previewUrl ? (
                                    <img src={previewUrl} alt="Preview" className="max-h-40 object-contain shadow-sm bg-white p-2 rounded" />
                                ) : (
                                    <div className="text-gray-400 text-sm italic">Completa los datos para ver la vista previa</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleGenerate}
                        disabled={!previewUrl}
                        className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${previewUrl ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'}`}
                    >
                        Insertar Código
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CodeGeneratorModal;
